import { VALTYPES } from '../../../../data/interfaces';
import { createColorMappingAspect, createColorMappingConfigs } from '../../helpers/colorMappingInfo';
import {
  createConfigParamBoolean,
  createConfigParamLabelSpace,
  createConfigParamMultipleChoice,
  createConfigParamTypeRange,
} from '../../helpers/configParams';
import { TpChartManifest } from '../interfaces';
import { AggregatedCoordinates } from './AggregatedCoordinates';
import { centerValueOptions, whiskersOptions } from './rendererOptions';

export const manifestAggregatedCoordinates: TpChartManifest = {
  id: 'AggregatedCoordinates',
  name: 'Aggregated coordinates plot',
  description: `Displays distribution information for a series of numerical properties. 
  For each property, the values of that property are aggregated (e.g. by calculating the average) 
  within the whole table or within categories defined by another categorical property.
  The aggregated values are connected by lines to show the trends across the different properties. 
  The Y-axis is scaled based on the minimum and maximum values across all properties, not based on the aggregated values, to allow better comparison of the distributions across the different properties.`,
  component: AggregatedCoordinates,
  canvasBased: true,
  groups: [
    { groupId: 'axes', groupName: 'Axes' },
    { groupId: 'appearance', groupName: 'Appearance' },
  ],
  aspects: [createColorMappingAspect('categories', 'categories data', 'axes', 0.5, 'lines', [VALTYPES.CATEGORICAL])],
  compoundAspects: [
    {
      id: 'coordinates',
      name: 'Coordinates',
      description: 'Set of properties to be displayed as coordinates.',
      groupId: 'axes',
      required: true,
      dataType: [VALTYPES.NUMBER, VALTYPES.INTEGER],
      minCount: 2,
    },
  ],
  configParams: [
    {
      id: 'displayType',
      name: 'Center display value',
      description: `Metric used to determine the center value of the distribution.`,
      groupId: 'axes',
      paramType: createConfigParamMultipleChoice(centerValueOptions),
    },
    {
      id: 'whiskers',
      name: 'Whiskers display value',
      description: `Metric used to determine the whiskers of the distribution.`,
      groupId: 'axes',
      paramType: createConfigParamMultipleChoice(whiskersOptions),
    },
    ...createColorMappingConfigs('categories', 'Categories', 'appearance', [VALTYPES.CATEGORICAL]),
    {
      id: 'categoriesConnect',
      name: 'Connect columns',
      description: 'Whether to connect the points of different columns with lines.',
      groupId: 'appearance',
      paramType: createConfigParamMultipleChoice([
        { id: 'always', name: 'Always' },
        { id: 'hover', name: 'On hover' },
        { id: 'never', name: 'Never' },
      ]),
    },
    {
      id: 'labelSpace',
      name: 'Label space',
      description:
        'Scaling factor that determines how much space in the chart is used by the names of the different properties.',
      groupId: 'appearance',
      paramType: createConfigParamLabelSpace(),
    },
    {
      id: 'pointSizeFactor',
      name: 'Point size',
      groupId: 'appearance',
      description: 'Scaling factor for the overall size of the points in the plot.',
      paramType: createConfigParamTypeRange(0.2, 4, 0.1, 1),
    },
    {
      id: 'opacity',
      name: 'Opacity',
      description: `Line opacity.
In case a plot contains many overlapping lines, lowering the opacity (i.e. increasing transparency)
can help reveal the data structure.`,
      groupId: 'appearance',
      paramType: createConfigParamTypeRange(0.1, 1, 0.1, 0.7),
    },
    {
      id: 'unselectedOpacity',
      name: 'Unselected opacity',
      groupId: 'appearance',
      description: `Unselected items opacity.`,
      paramType: createConfigParamTypeRange(0.01, 1, 0.1, 0.5),
    },
    {
      id: 'scatterXAxis',
      name: 'X-axis jitter',
      description: 'Separation of points along the X-axis to reduce overlap.',
      groupId: 'appearance',
      paramType: createConfigParamBoolean(false),
      hidden: true, // Only for dashboard builders
    },
  ],
  examples: [
    {
      id: 'aggregated_coordinates_1',
    },
  ],
};
